import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import HomeHeroCarousel from "./HomeHeroCarousel";
import Button from "@components/button/Button";
import ChevronLarge from "@icons/ChevronLarge";
import cn from "classnames";
import useHeaderMargin from "@hooks/useHeaderMargin";
import { motion, useAnimationControls } from "framer-motion";
import useIsRTL from "@hooks/useIsRTL";
import MobileSwiper from "@components/swiper/MobileSwiper";
import useScreenDimensions from "@hooks/useScreenDimensions";
import { filterBgImgUrl } from "@utils/filtering";
import useScrollTo from "@hooks/useScrollTo";
const HomeHero = ({ homeHero }) => {
    var _a, _b;
    if (!homeHero || homeHero.length < 1) {
        return null;
    }
    const coverEl = useRef(null);
    const isMounted = useRef(false);
    const [selectedSlide, setSelectedSlide] = useState(homeHero[0]);
    const mainDiv = useRef();
    const { headerHeight } = useHeaderMargin();
    const textControls = useAnimationControls();
    const triangleControls = useAnimationControls();
    const { isRtl } = useIsRTL();
    const { width: screenWidth, height: screenHeight } = useScreenDimensions();
    const { scrollTo } = useScrollTo();
    const [textSectionStyles, setTextSectionStyles] = useState();
    const [leftSectionStyles, setLeftSectionStyles] = useState();
    const [titleStyles, setTitletyles] = useState();
    const [descriptionStyles, setDescriptionStyles] = useState();
    useEffect(() => {
        const isLowScreen = screenHeight < 768;
        setTextSectionStyles(isLowScreen ? { marginTop: "16px" } : {});
        setLeftSectionStyles(isLowScreen ? { minHeight: "45vh", gap: "8px" } : {});
        setTitletyles(isLowScreen ? { fontSize: "5.2vh", lineHeight: "32px" } : {});
        setDescriptionStyles(isLowScreen ? { fontSize: "2.8vh", lineHeight: "24px" } : {});
    }, [screenHeight]);
    const onSlideChange = (index) => {
        // skip slide change on initial load
        if (!isMounted.current && index === 0) {
            isMounted.current = true;
            return;
        }
        textControls.start((i) => ({
            x: isRtl ? "125%" : "-125%",
            transition: { delay: i * 0.3, duration: 0.5 }
        }));
        triangleControls.start(() => ({
            top: "-200rem",
            transition: { delay: 0.3, duration: 0.5 }
        }));
        setTimeout(() => {
            setSelectedSlide(homeHero[index]);
        }, 1000);
        setTimeout(() => {
            textControls.start((i) => ({
                x: "0",
                transition: { delay: i * 0.3, duration: 0.5 }
            }));
            triangleControls.start(() => ({
                top: "3rem",
                transition: { delay: 0.3, duration: 0.5 }
            }));
        }, 1200);
    };
    const onSlideDownClick = () => {
        var _a;
        const nextEl = (_a = mainDiv.current.nextElementSibling) !== null && _a !== void 0 ? _a : mainDiv.current.parentElement.nextElementSibling;
        if (nextEl) {
            scrollTo(nextEl);
        }
    };
    const getMaxCarouselWidth = () => {
        let items = homeHero.length;
        if (screenWidth < 1200 && items > 2) {
            return 500;
        }
        return items < 4 ? (items === 2 ? 210 * items : 180 * items) : 672;
    };
    const calculateImageWrapperWidth = () => {
        let screenH = 1000;
        let additionalWidthForLowScreens = 0;
        if (screenHeight > 0) {
            additionalWidthForLowScreens = screenHeight > screenH ? 0 : 300;
        }
        if (screenWidth > 2500) {
            return 600;
        }
        if (screenWidth < 1150) {
            return -100;
        }
        if (screenWidth < 1280) {
            return 0;
        }
        if (screenWidth < 1310 || screenHeight > 1310) {
            return 100;
        }
        if (screenWidth < 1370 || screenHeight > 1370) {
            return additionalWidthForLowScreens;
        }
        if (screenWidth < 1500) {
            return 80 + additionalWidthForLowScreens;
        }
        return screenWidth / 5;
    };
    const calculateTextSectionWIdth = () => {
        // width check
        if (screenWidth > 1800) {
            return 40;
        }
        // height check
        if (screenHeight < 1000 || screenHeight > 1440 || screenHeight > 1200) {
            return 40;
        }
        return 50;
    };
    return (_jsxs("div", { className: "home-hero", ref: mainDiv, children: [selectedSlide && (_jsx(_Fragment, { children: _jsxs("div", { className: "home-hero__cover hidden lg:flex", ref: coverEl, style: { paddingBottom: "15vw" }, children: [_jsxs("div", { className: "home-hero__left-section", children: [_jsx("div", { className: "home-hero__white-angled-bg", style: { [isRtl ? "left" : "right"]: `${calculateImageWrapperWidth()}px` } }), _jsxs("div", { className: "flex flex-col gap-10 lg:min-h-[460px] xl:min-h-[480px] 3xl:min-h-[440px]", style: leftSectionStyles, children: [_jsxs("div", { className: "home-hero__left-section-text", style: { ...textSectionStyles, width: `${calculateTextSectionWIdth()}vw` }, children: [_jsx(motion.div, { className: "home-hero__left-section-title", custom: 0.5, animate: textControls, style: titleStyles, children: (_a = selectedSlide.title) !== null && _a !== void 0 ? _a : "" }), _jsx(motion.div, { className: "home-hero__left-section-description", custom: 1, animate: textControls, style: descriptionStyles, children: (_b = selectedSlide.description) !== null && _b !== void 0 ? _b : "" })] }), _jsx(motion.div, { className: "home-hero__left-section-button", custom: 1.5, animate: textControls, children: selectedSlide.button && selectedSlide.button.title && selectedSlide.button.url && (_jsx(Button, { title: selectedSlide.button.title, link: selectedSlide.button.url, className: "whitespace-nowrap" })) })] }), homeHero.length > 1 && (_jsx("div", { className: "home-hero__left-section-carousel-section", style: { maxWidth: `${getMaxCarouselWidth()}px` }, children: _jsx(HomeHeroCarousel, { items: homeHero, onSlideChange: onSlideChange }) }))] }), _jsx("div", { className: "home-hero__right-section overflow-hidden", children: _jsx("div", { className: cn("home-hero__right-section-img"), style: { backgroundImage: `url('${filterBgImgUrl(selectedSlide.imageURL)}')` } }) }), _jsx(motion.div, { className: cn(`home-hero__red-angled-container`), animate: triangleControls }), _jsx("div", { className: "absolute left-0 right-0 z-[4] mx-auto w-10 animate-bounce", style: { bottom: `calc(20px + ${headerHeight}px + 15vw)` }, children: _jsx("div", { className: "hidden cursor-pointer justify-center lg:flex", onClick: () => onSlideDownClick(), children: _jsx(ChevronLarge, {}) }) })] }) })), _jsx("div", { className: "flex max-w-full overflow-hidden lg:hidden", children: _jsx(MobileSwiper, { data: homeHero, loop: homeHero.length > 1, className: "home-hero-mobile", displayData: (selectedSlide) => {
                        var _a, _b;
                        return (_jsxs("div", { className: "home-hero__mobile-slide", children: [_jsx("div", { className: "home-hero__top-section", children: _jsxs("div", { className: "home-hero__top-section-text", children: [_jsxs("div", { className: "home-hero__left-section-text", children: [_jsx("div", { className: "home-hero__left-section-title", children: (_a = selectedSlide.title) !== null && _a !== void 0 ? _a : " " }), _jsx("div", { className: "home-hero__left-section-description", children: (_b = selectedSlide.description) !== null && _b !== void 0 ? _b : " " })] }), _jsx("div", { className: "home-hero__left-section-button", children: selectedSlide.button && selectedSlide.button.title && selectedSlide.button.url && (_jsx(Button, { title: selectedSlide.button.title, link: selectedSlide.button.url, className: "whitespace-nowrap line-clamp-1" })) })] }) }), _jsx("div", { className: "home-hero__mobile-img-wrapper", children: _jsx("img", { className: "home-hero__mobile-img", src: selectedSlide.mobileImageURL, alt: selectedSlide.imageAltText }) })] }));
                    } }) })] }));
};
export default HomeHero;
